// Boxing-specific taxonomy + constants for the Combatives Detailed Journal.
// Drafted per /app/memory/BOXING_RESEARCH.md, founder-approved.
// Architecture: Boxing ≈ Muay Thai minus kicks/knees/elbows/clinch. Schema
// mirrors MT for shared elements (rounds, sparring, defense, footwork).
// CITED SOURCES (per founder direction — credibility/defensibility):
//   - USA Boxing Coaches Manual & Rulebook (current edition)
//     https://www.usaboxing.org/coaches
//   - AIBA / World Boxing International Rules
//     https://www.worldboxing.org/rules
//   - Mayweather Boxing Club training materials (modern 1-6 numbering)
//   - Cus D'Amato peek-a-boo system (head movement taxonomy)

export const BOXING_STANCES = [
  { id: 'orthodox', label: 'Orthodox' },
  { id: 'southpaw', label: 'Southpaw' },
  { id: 'switch',   label: 'Switch (training both)' },
];

export const BOXING_CLASS_FORMATS = [
  { id: 'shadow',        label: 'Shadow boxing' },
  { id: 'mitt',          label: 'Mitt work' },
  { id: 'pad',           label: 'Pad work' },
  { id: 'heavy_bag',     label: 'Heavy bag' },
  { id: 'speed_bag',     label: 'Speed bag' },
  { id: 'double_end',    label: 'Double-end bag' },
  { id: 'sparring',      label: 'Sparring' },
  { id: 'conditioning',  label: 'Conditioning' },
  { id: 'fight_prep',    label: 'Fight prep' },
  { id: 'film_scouting', label: 'Film / Scouting' },
  { id: 'match_bout',    label: 'Match / Bout' },
  { id: 'tournament',    label: 'Tournament' },
  { id: 'open_gym',      label: 'Open gym' },
];

export const BOXING_FOCUS_MODES = ['Drilling', 'Sparring', 'Mastery', 'Conditioning', 'Recovery'];
export const BOXING_SPARRING_TRIGGERS = ['Sparring'];

export const BOXING_MASTERY_LEVELS = [
  { value: 'Studying',     label: 'Studying',     help: "Watched, haven't drilled" },
  { value: 'Drilling',     label: 'Drilling',     help: "Slow with a willing partner" },
  { value: 'Working',      label: 'Working',      help: 'Hitting it sometimes in sparring' },
  { value: 'Landing Live', label: 'Landing Live', help: 'Consistent in live sparring' },
  { value: 'Default Move', label: 'Default Move', help: 'Reflexive — your go-to' },
];

export const BOXING_FELT_SCALE = [
  { value: 1, label: 'Got smashed' },
  { value: 2, label: 'Held my own' },
  { value: 3, label: 'Even' },
  { value: 4, label: 'Pushed the pace' },
  { value: 5, label: 'Dominated' },
];

// Three top-level technique groups: Offense / Defense / Footwork.
// Numbering 1-6 is the universal coach shorthand. src: Mayweather/USA Boxing.
export const BOXING_GROUPS = [
  { id: 'offense',  label: 'Offense' },
  { id: 'defense',  label: 'Defense' },
  { id: 'footwork', label: 'Footwork' },
];

export const BOXING_TECHNIQUES = [
  { group: 'offense', subgroup: 'Power Punches', techniques: [
    'Jab (1)', 'Cross (2)', 'Lead hook (3)', 'Rear hook (4)', 'Lead uppercut (5)', 'Rear uppercut (6)',
  ]},
  { group: 'offense', subgroup: 'Body Shots', techniques: [
    'Jab to body', 'Cross to body', 'Lead hook to body', 'Rear hook to body',
    'Lead uppercut to body', 'Rear uppercut to body', 'Shovel hook',
  ]},
  { group: 'offense', subgroup: 'Specialty Punches', techniques: [
    'Overhand right', 'Overhand left', 'Check hook', 'Bolo punch',
    'Liver shot', 'Solar plexus shot',
  ]},
  { group: 'defense', subgroup: 'Head Movement', techniques: [
    'Slip outside', 'Slip inside', 'Roll under', 'Lean back', 'Pull', 'Sway', 'Bob and weave',
  ]},
  { group: 'defense', subgroup: 'Hand Defense', techniques: [
    'High guard', 'Cross-arm guard (Philly shell)', 'Parry inside', 'Parry outside',
    'Block (forearm)', 'Block (glove)', 'Catch',
  ]},
  { group: 'defense', subgroup: 'Shoulder Defense', techniques: [
    'Shoulder roll', 'Shoulder bump',
  ]},
  { group: 'footwork', subgroup: 'Movement', techniques: [
    'Step in', 'Step out', 'Lateral step (L)', 'Lateral step (R)',
    'Pivot inside', 'Pivot outside', 'Pendulum step', 'Skip step',
  ]},
  { group: 'footwork', subgroup: 'Angles', techniques: [
    '45° angle change', 'V-step', 'Cut the ring', 'Circle out', 'Push pivot',
  ]},
  { group: 'footwork', subgroup: 'Distance Management', techniques: [
    'Lengthen (long range)', 'Shorten (close range)', 'Fade (out of range)', 'Crash (close hard)',
  ]},
];

export function boxingSubgroupsForGroup(groupId) {
  return BOXING_TECHNIQUES.filter((t) => t.group === groupId).map((t) => t.subgroup);
}
export function boxingTechniquesFor(groupId, subgroup) {
  const found = BOXING_TECHNIQUES.find((t) => t.group === groupId && t.subgroup === subgroup);
  return found ? found.techniques : [];
}

export const BOXING_GLOVE_OPTIONS = [10, 12, 14, 16, 18, 20];
export const BOXING_PARTNER_LEVELS = [
  { id: 'newer',           label: 'Newer than me' },
  { id: 'similar',         label: 'Similar level' },
  { id: 'more_experienced', label: 'More experienced' },
];

export const BOXING_COACH_TOPICS = [
  'Hand position', 'Footwork', 'Head movement', 'Combinations',
  'Defense', 'Body work', 'Conditioning', 'Mental', 'Weight', 'Match strategy', 'Other',
];

// Founder decision: same W/L/Draw simplicity as wrestling. If pros
// arrive and want ko/tko/decision granularity, we add then.
export const BOXING_MATCH_RESULTS = [
  { id: 'win',  label: 'Win' },
  { id: 'loss', label: 'Loss' },
  { id: 'draw', label: 'Draw' },
];

// Weight block constants — mirror wrestling structure. Founder approved
// `collapsed by default` UX so it stays out of the way for fitness boxers.
export const BOXING_CUT_METHODS = [
  { id: 'gradual',     label: 'Gradual diet',     help: 'Calorie deficit over weeks — safest' },
  { id: 'water_cycle', label: 'Water cycling',    help: 'Loading/cutting water in the 72hr window' },
  { id: 'sweat',       label: 'Sweat (sauna / mat / runs)', help: 'Acute water loss in 24hr window' },
  { id: 'mixed',       label: 'Mixed methods',    help: 'Combination of the above' },
];
export const BOXING_HYDRATION = [
  { id: 'green', label: 'Green', help: 'Urine pale, energy normal, no cramping' },
  { id: 'amber', label: 'Amber', help: 'Urine darker, mild thirst, watching closely' },
  { id: 'red',   label: 'Red',   help: 'Dark urine, headache, cramping — STOP cutting' },
];
export const BOXING_WEIGHT_FLAGS = { rapidDropLbsPerDay: 3, riskyProjectedCutPct: 5 };
export const BOXING_WEIGHT_REFERENCES = [
  { label: 'USA Boxing weight management', url: 'https://www.usaboxing.org/', blurb: 'Sanctioning body weight guidance.' },
  { label: 'NCAA-style 1.5%/week cut guidance', url: 'https://www.ncaa.org/sports/2014/12/11/wrestling-weight-management-program.aspx', blurb: 'Cross-applicable safe-cut floor.' },
];
